import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { FormRadioGroupInput } from "form";
import { actionInputName } from "components/action-modal/utils/inputName";
import { ACTION_VIEW_MODE, TASK_INPUT_NAMES } from "constants/action-form";
import { useActionModalGetContext } from "context/ActionModalProvider";

export const BIO_SELECTIONS = [
  {
    value: false,
    label: {
      nameGr: "Συμβατική",
      nameEn: "Conventional",
    },
  },
  {
    value: true,
    label: {
      nameGr: "Βιολογική",
      nameEn: "Biological",
    },
  },
];

/**
 *
 * @param {Object} props
 * @param {import("constants/actions").Task} props.task - The task for which the bio selection is being made (e.g., "fertilize").
 * @param {boolean} props.isProposed - Indicates if the value is proposed or current.
 * @param {boolean} props.isViewMode - Indicates if the component is in view mode.
 * @returns
 */
const BioSelectionInput = ({ task, isProposed, isViewMode }) => {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const { action, fieldInfo } = useActionModalGetContext();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const taskInputName = TASK_INPUT_NAMES[task];
  const bioInputName = actionInputName(taskInputName, "bio", isProposed);
  const selectedBioValue =
    values?.[isProposed ? "proposedAction" : taskInputName]?.bio;
  const isBioCrop = Boolean(fieldInfo?.isBioCrop);
  const shouldLockAsBio =
    action?.type === ACTION_VIEW_MODE.new && isBioCrop === true;
  const wasLockedAsBio = useRef(shouldLockAsBio);

  useEffect(() => {
    const previouslyLockedAsBio = wasLockedAsBio.current;
    wasLockedAsBio.current = shouldLockAsBio;

    if (shouldLockAsBio && selectedBioValue !== true) {
      setFieldValue(bioInputName, true).then(() => {
        setFieldTouched(bioInputName, true);
      });
      return;
    }

    if (
      previouslyLockedAsBio &&
      !shouldLockAsBio &&
      selectedBioValue === true
    ) {
      setFieldValue(bioInputName, false).then(() => {
        setFieldTouched(bioInputName, true);
      });
    }
  }, [
    bioInputName,
    selectedBioValue,
    setFieldTouched,
    setFieldValue,
    shouldLockAsBio,
  ]);

  return (
    <FormRadioGroupInput
      name={bioInputName}
      shouldHaveErrorBehaviorAfterSubmit
      isViewMode={isViewMode}
      isDisabled={shouldLockAsBio}
      options={BIO_SELECTIONS.map(({ value, label: { nameEn, nameGr } }) => ({
        value,
        label: isEn ? nameEn : nameGr,
      }))}
    />
  );
};

export default BioSelectionInput;
